import {
	RateCardType,
	TRateCardModifierSet,
	TRateCardHandUnloadModifier,
	TRateCardModifierType,
} from '@types';
import {
	ContentExpander,
	Grid,
	NumberField,
	Label,
	Radio,
	Spacer
} from '@components';
import styles from './Modifiers.module.scss';
import { useRecoilValue } from 'recoil';
import {
	RateCardFormErrors, RateCardSettingsTypes, RateCardTypes, AllRateCardFuelLevyModifiers, AllRateCardSettingFuelLevyModifiers
} from '@state';
import { useClearRCSettingsError, useRateCardModifiersState } from '@hooks';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { getRateCardIndex, removeLeadingZeros } from '@utils';
import { ModifierBadge } from './ModifierBadge';

type TProps = {
	isSettings?: boolean;
	type: RateCardType
	isDisabled?: boolean;
	itemTypeId?: string;
}

export const HandUnloadModifierPanel = (props: TProps) => {
	const {
		isSettings,
		type,
		isDisabled = false,
		itemTypeId
	} = props;
	const [rateCardModifiers, setRateCardModifiers] = useRateCardModifiersState();
	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		itemTypeId,
		isSettings: !!isSettings,
		type
	});
	const modifiers = isSettings ? rateCardModifiers.settings : rateCardModifiers.rateCard;
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const clearRCSettingsError = useClearRCSettingsError();

	const allRateCardModifiers = useRecoilValue(AllRateCardFuelLevyModifiers);
	const allRateCardSettingModifiers = useRecoilValue(AllRateCardSettingFuelLevyModifiers);
	const fuelLevyModifiers = isSettings ? allRateCardSettingModifiers : allRateCardModifiers;

	const updateTimeSlotModifier = (
		key: keyof TRateCardHandUnloadModifier,
		newValue: string,
	) => {
		const update = {
			...modifiers,
			hand_unload: newValue === 'None' ? null : {
				...modifiers?.hand_unload,
				...(key === 'type' ? { value: '' } : {}),
				[key]: String(newValue),
				attracts_fuel_levy: !!fuelLevyModifiers?.hand_unload?.attracts_fuel_levy
			}
		} as TRateCardModifierSet;

		const newModifiers = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined,
		}

		setRateCardModifiers(newModifiers);
		// @ts-ignore
		if (rcFormErrors?.[errorString]?.[rateCardTypeIndex].modifier_set.hand_unload[key]) {
			clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.modifier_set.hand_unload.${key}`)
		}
	}

	const handleOnBlur = (
		modifierKey: keyof TRateCardHandUnloadModifier,
		newValue: string,
	) => {
		if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
			const value = removeLeadingZeros(newValue);
			updateTimeSlotModifier(modifierKey, value);
		}
		if (newValue.startsWith('.')) {
			updateTimeSlotModifier(modifierKey, `0${newValue}`)
		}
	}

	const hasError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.hand_unload?.[key]?._errors?.[0] || '';
	}

	const getSectionErrors = () => {
		const fieldsWithPossibleError = ['value']
		return fieldsWithPossibleError.map((field) =>
			hasError(field)
		).some((field) => field.length > 0);
	}

	const getFeeLabel = () => {
		return modifiers?.hand_unload?.type === TRateCardModifierType.PER_PALLET ? 'Fee per pallet' : 'Fee per stop';
	}

	const getConfiguredCount = () => {
		if (!modifiers?.hand_unload) return 0;
		return parseFloat(`${modifiers.hand_unload.value ?? ''}`) > 0 ? 1 : 0;
	}

	if (!modifiers) return

	return (
		<ContentExpander
			title={<div className={styles.expanderTitle}>Hand Unload
				<ModifierBadge count={getConfiguredCount()} />
				{
					getSectionErrors() ?
						<span className={styles.expanderErrorContainer}>
							<ErrorOutlineOutlinedIcon />
						</span>	: null
				}
			</div>}
			showSpacer
			expanded={false}
			arrowsAsExpanderIcon
			iconColor='var(--neutral-06)'
		>
			<div className={styles.expanderContent}>
				<Label>Charge method</Label>
				<Radio.Group className={styles.radioContainer}>
					<Radio
						name={'charge_method'}
						label={'Fee per stop'}
						value={TRateCardModifierType.PER_STOP}
						onChange={(e) => updateTimeSlotModifier('type', e.target.value)}
						checked={modifiers?.hand_unload?.type === TRateCardModifierType.PER_STOP}
						disabled={isDisabled}
					/>
					<Radio
						name={'charge_method'}
						label={'Fee per pallet'}
						value={TRateCardModifierType.PER_PALLET}
						onChange={(e) => updateTimeSlotModifier('type', e.target.value)}
						checked={modifiers?.hand_unload?.type === TRateCardModifierType.PER_PALLET}
						disabled={isDisabled}
					/>
					<Radio
						name={'charge_method'}
						label={'None'}
						value={'None'}
						onChange={(e) => updateTimeSlotModifier('type', e.target.value)}
						checked={modifiers?.hand_unload === null}
						disabled={isDisabled}
					/>
				</Radio.Group>
				{
					modifiers?.hand_unload?.type &&
						<Grid columns={3} columnTemplate={'repeat(3, 1fr)'} gap={'16px'} alignItems='start'>
							<NumberField
								name={'value'}
								value={`${modifiers.hand_unload?.value ?? ''}`}
								onChange={(e) => updateTimeSlotModifier('value', e.target.value)}
								leftModule={<span className={styles.prefix}>$</span>}
								label={getFeeLabel()}
								keepErrorSpace={false}
								required
								hasError={!!hasError('value')}
								errorMessage={hasError('value')}
								disabled={isDisabled}
								min={'0'}
								onBlur={(e) => handleOnBlur('value', e.target.value)}
							/>
						</Grid>
				}
				<Spacer size={5} />
			</div>

		</ContentExpander>
	);
}